// TMOS Admin Login Page
// Date: November 15, 2025
// Purpose: Special login page for admins during maintenance mode

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/state/auth/AuthProvider';
import { maintenanceModeService } from '@/services/maintenanceModeService';
import { toast } from 'react-hot-toast';
import { EnvelopeIcon, LockClosedIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';

export const AdminLoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [maintenanceActive, setMaintenanceActive] = useState(false);
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    checkMaintenanceStatus();
  }, []);

  const checkMaintenanceStatus = async () => {
    try {
      const status = await maintenanceModeService.getStatus();
      setMaintenanceActive(status.is_active);
    } catch (error) {
      console.error('Error checking maintenance status:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const authResponse = await login({ email, password });
      
      // After login, check if user is platform admin
      // The login function returns the auth response with user data
      const userRole = authResponse.user?.role;
      
      const isPlatformAdmin = userRole === 'Platform Administrator' || userRole === 'PLATFORM_ADMIN';
      
      if (!isPlatformAdmin) {
        // Not a platform admin - log them out and show error
        await logout();
        toast.error(t('auth.adminOnly') || 'This login page is only for Platform Administrators');
        setIsLoading(false);
        return;
      }
      
      // Platform admin - allow access
      toast.success(t('auth.loginSuccessful') || 'Login successful');
      navigate('/dashboard');
      setIsLoading(false);
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.message || t('auth.loginFailed') || 'Login failed');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-primary-bg)] flex flex-col">
      {/* Header */}
      <div className="bg-[var(--color-primary-bg)] py-4">
        <div className="flex justify-center items-center">
          <div className="flex items-center">
            <ShieldCheckIcon className="h-8 w-8 text-[var(--color-primary-text)]" />
            <span className="ml-2 text-2xl font-bold text-[var(--color-primary-text)]">
              {t('auth.adminBrand') || 'T-MOS Admin'}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content - Centered */}
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md w-full space-y-8 text-center">
          {/* Welcome Text */}
          <div>
            <h1 className="text-4xl font-bold text-[var(--color-primary-text)] mb-2">
              {t('auth.adminLogin') || 'Admin Login'}
            </h1>
            <p className="text-lg text-[var(--color-primary-text)]">
              {maintenanceActive 
                ? (t('auth.adminLoginDuringMaintenance') || 'Admin access during maintenance')
                : (t('auth.adminAccess') || 'Administrator access')
              }
            </p>
          </div>

          {/* Admin Icon */}
          <div className="flex justify-center">
            <div className="w-20 h-20 bg-[var(--color-primary-text)] rounded-full flex items-center justify-center">
              <ShieldCheckIcon className="h-10 w-10 text-[var(--color-primary-bg)]" />
            </div>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div className="flex items-center space-x-3">
              <EnvelopeIcon className="h-5 w-5 text-[var(--color-primary-text)] flex-shrink-0" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.emailPlaceholder') || 'Email'}
                className="flex-1 py-3 px-3 border-2 border-[var(--color-primary-text)] rounded-lg bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] placeholder-[var(--color-secondary-text)] placeholder-opacity-60 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)] focus:border-transparent text-center"
                required
              />
            </div>

            {/* Password Field */}
            <div className="flex items-center space-x-3">
              <LockClosedIcon className="h-5 w-5 text-[var(--color-primary-text)] flex-shrink-0" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.passwordPlaceholder') || 'Password'}
                className="flex-1 py-3 px-3 border-2 border-[var(--color-primary-text)] rounded-lg bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] placeholder-[var(--color-secondary-text)] placeholder-opacity-60 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)] focus:border-transparent text-center"
                required
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 bg-[var(--color-primary-text)] text-[var(--color-primary-bg)] rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (t('auth.loggingIn') || 'Logging in...') : (t('auth.login') || 'Login')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};


